import { useCallback, useMemo } from "react";
import { useApp } from "../context/AppState";
import {
  buildProductLinkAttributionToken,
  buildStorefrontProductPath,
  isLinkToProductEnabled,
  isLinkToProductOpenInNewTab,
  navigateToStorefrontProduct,
} from "../../shared/product-link.js";
import { useAnalytics } from "./useAnalytics";
import { trackClick } from "../utils/analyticsUtils";

export default function useProductLink({
  popupData,
  productData,
  clickTarget = "image",
}) {
  const { appState } = useApp();
  const { addAnalyticsEvent } = useAnalytics();

  const linkEnabled = useMemo(
    () => isLinkToProductEnabled(popupData) && !appState?.is_preview_mode,
    [popupData, appState?.is_preview_mode]
  );

  const openInNewTab = useMemo(
    () => isLinkToProductOpenInNewTab(popupData),
    [popupData]
  );

  const attribution = useMemo(() => {
    if (!linkEnabled) {
      return null;
    }
    const shop = appState?.shop;
    return buildProductLinkAttributionToken({
      popupId: popupData?._id ?? popupData?.id ?? popupData?.popupId,
      campaignId: popupData?.campaign_Id,
      productId: productData?.id ?? productData?.admin_graphql_api_id,
      popupType: popupData?.popUpType,
      shop,
    });
  }, [linkEnabled, popupData, productData, appState?.shop]);

  const path = useMemo(() => {
    if (!linkEnabled) {
      return null;
    }
    return buildStorefrontProductPath(productData, {
      attribution: attribution || undefined,
    });
  }, [linkEnabled, productData, attribution]);

  const navigate = useCallback(
    (event) => {
      if (!path) {
        return;
      }
      event?.preventDefault?.();
      event?.stopPropagation?.();

      const popupId = popupData?._id ?? popupData?.id ?? popupData?.popupId;
      void trackClick({
        popupId,
        popupType: popupData?.popUpType || popupData?.template_id?.title,
        campaignId: popupData?.campaign_Id,
        clickTarget,
        actionType: "product_link",
        productId: productData?.id ?? productData?.admin_graphql_api_id,
        addAnalyticsEvent,
      });

      navigateToStorefrontProduct(productData, {
        newTab: openInNewTab,
        attribution: attribution || undefined,
      });
    },
    [
      path,
      productData,
      openInNewTab,
      attribution,
      popupData,
      clickTarget,
      addAnalyticsEvent,
    ]
  );

  return {
    isClickable: Boolean(path),
    path,
    navigate,
  };
}
